import { Injectable } from '@angular/core';
import { combineQueries } from '@datorama/akita';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AclAssetPermission } from '@y42/acl/models';
import { AbstractFeatureQueryEntity, Y42FeatureToAclAssetResourceType } from '@y42/feature';
import { FeatureAclQuery } from '@y42/feature/acl';
import { Y42FeatureType } from '@y42/json-schema';
import { assertNonNullable } from '@y42/util/assert';
import { debounceTime, filter, map, Observable, shareReplay } from 'rxjs';
import { OrchestrationListViewData, OrchestrationState, OrchestrationUIModel } from './orchestration.model';
import { OrchestrationStore } from './orchestration.store';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class OrchestrationQuery extends AbstractFeatureQueryEntity<OrchestrationState, OrchestrationUIModel> {
  constructor(protected readonly store: OrchestrationStore, private readonly featureAclQuery: FeatureAclQuery) {
    super({
      store,
      featureType: Y42FeatureType.Orchestration,
      toUIModel: (storeModel) => {
        const resourceType = Y42FeatureToAclAssetResourceType[Y42FeatureType.Orchestration];
        assertNonNullable(resourceType);

        return {
          ...storeModel,
          featureType: Y42FeatureType.Orchestration,
          last_used: this.getValue().last_used?.[storeModel.$path] || null,
          starred: !!this.getValue().starred[storeModel.$path],
          hasReadPermission: !!this.featureAclQuery.getPermission({
            resourceType,
            assetPath: storeModel.$path,
            permission: AclAssetPermission.Read,
          })?.allowed,
        };
      },
    });
  }

  readonly items$: Observable<OrchestrationUIModel[]> = combineQueries([
    this.selectAll(),
    this.select((st) => st.last_used),
    this.select((st) => st.starred),
  ]).pipe(
    debounceTime(0),
    map(([orchestrations]) => orchestrations.map((orchestration) => this.toUIModel(orchestration))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly listItems$: Observable<OrchestrationListViewData[]> = combineQueries([
    this.loading$,
    this.items$,
    this.select((st) => st.last_used),
    this.select((st) => st.starred),
  ]).pipe(
    filter(([loading]) => !loading),
    map(([_, orchestrations]) => orchestrations.map(this.toListViewModel)),
    map((arr) => arr.flat()),
  );

  readonly active$: Observable<OrchestrationUIModel | undefined> = combineQueries([
    this.selectActive(),
    this.select((st) => st.last_used),
    this.select((st) => st.starred),
  ]).pipe(
    map(([orchestration]) => (orchestration ? this.toUIModel(orchestration) : undefined)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly toListViewModel = (orchestration: OrchestrationUIModel): OrchestrationListViewData[] => {
    return [{ ...orchestration, id: orchestration.$path }];
  };
}
